import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useState } from "react";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { useSetValidTransportationTypeQuery } from "@/features/BookingWizard/components/WizardStepContainer/steps/AddPlayersStep/hooks/__generated__/useSetValidTransportationTypeQuery.graphql";
import { getDynamicLockedRoundIdsAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getDynamicLockedRoundIdsAtom/getDynamicLockedRoundIdsAtom";
import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { useGetBundledRateOptionsQuery } from "../../../../../../../../../../../../../../../hooks/__generated__/useGetBundledRateOptionsQuery.graphql";
import {
  getBundledRateTypeFromClientType,
  getBundledTransportationPlayerTypeFromClientType,
  useBundledRateOptionsQuery,
  useGetBundledRateOptions,
  useGetBundledRateOptionsQueryString,
} from "../../../../../../../../../../../../../../../hooks/useGetBundledRateOptions";
import { useGetSingleBundleRateOption } from "../../../../../../../../../../../../../../../hooks/useGetSingleBundleRateOption";
import { setPlayerRateOnRoundAtom } from "../../../../../../../../../../atoms/actions/setPlayerRateOnRound/setPlayerRateOnRoundAtom";
import { setTransportationTypeOnRoundAtom } from "../../../../../../../../../../atoms/actions/setTransportationTypeOnRound/setTransportationTypeOnRoundAtom";
import { getCourseLayoutFromRoundIdAtom } from "../../../../../../../../../../atoms/selectors/getCourseLayoutFromRoundIdAtom/getCourseLayoutFromRoundIdAtom";
import { getRateFromRoundIdAtom } from "../../../../../../../../../../atoms/selectors/getRateFromRoundIdAtom/getRatefromRoundIdAtom";
import { getRoundFromIdAtom } from "../../../../../../../../../../atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import {
  currentBookingAtom,
  currentSessionMemberShipTypeIdAtom,
  currentSessionUserClientIdAtom,
  defaultMembershipTypeIdAtom,
} from "../../../../../../../../../../atoms/stores";
import { getTransportationTypeFromRoundIdAtom } from "../TransportationTypeControl/selectors/getTransportationTypeFromRoundIdAtom";
import { getTargetTransportationRoundIds } from "../hooks/getTargetTransportationRoundIds";
import {
  useRoundTransportationTypes,
  useRoundTransportationTypesFromQuery,
} from "../hooks/useRoundTransportationTypes";
import {
  BundledRateAndTransportSelect_Booking$data,
  BundledRateAndTransportSelect_Booking$key,
} from "./__generated__/BundledRateAndTransportSelect_Booking.graphql";

const getMembershipTypeIdForPlayerId = (
  booking: BundledRateAndTransportSelect_Booking$data,
  playerId?: string | null
) => {
  if (!playerId) return null;

  const round = booking.rounds?.edges?.find(
    (edge) => edge?.node?.player?.id === playerId
  )?.node;

  return round?.player?.membershipType?.id ?? null;
};

const getMembershipTypeIdForRound = (
  booking: BundledRateAndTransportSelect_Booking$data,
  roundServerId?: string | null
) => {
  if (!roundServerId) return null;

  const round = booking.rounds?.edges?.find(
    (edge) => edge?.node?.id === roundServerId
  )?.node;

  return round?.player?.membershipType?.id ?? null;
};

type Props = {
  bookingRef: BundledRateAndTransportSelect_Booking$key;
  roundId: string;
};
export const BundledRateAndTransportSelect = ({
  bookingRef,
  roundId,
}: Props) => {
  const booking = useFragment(
    BundledRateAndTransportSelectFragmentString,
    bookingRef
  );
  const [isTargetingAll, setIsTargetingAll] = useState(false);
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentSessionMemberShipTypeId = useAtomValue(
    currentSessionMemberShipTypeIdAtom
  );
  const currentSessionUserClientId = useAtomValue(
    currentSessionUserClientIdAtom
  );
  const defaultMembershipTypeId = useAtomValue(defaultMembershipTypeIdAtom);
  const { queryRef } = useRoundTransportationTypes({
    slotId: currentBooking?.startSlotId,
    round: currentRound,
    currentSessionUserClientId,
  });
  const currentRoundMembershipTypeId = (() => {
    if (!currentRound) return null;

    const clientType = currentRound.player.clientType;
    if (clientType === "customer") {
      return currentRound.player.membershipTypeId;
    }

    if (clientType === "unknownCustomer") {
      return defaultMembershipTypeId ?? null;
    }

    if (clientType === "member") {
      return (
        currentRound.player.membershipTypeId ??
        getMembershipTypeIdForRound(booking, currentRound.serverId) ??
        currentSessionMemberShipTypeId ??
        null
      );
    }

    if (clientType === "memberHold") {
      return currentSessionMemberShipTypeId ?? null;
    }

    return (
      getMembershipTypeIdForPlayerId(booking, currentRound.hostId) ??
      currentSessionMemberShipTypeId ??
      null
    );
  })();
  const hostingRound = currentBooking?.rounds.find(
    (round) => round.player.id === currentRound?.hostId
  );
  const { queryRef: rateOptionsQueryRef } = useBundledRateOptionsQuery({
    timeSlotId: currentBooking?.startSlotId,
    membershipTypeId: currentRoundMembershipTypeId,
    visibleToMembershipTypeId: currentRoundMembershipTypeId,
    rateType: getBundledRateTypeFromClientType(currentRound?.player.clientType),
    nonPlayingHost:
      currentRound?.player.clientType === "guest" ||
      currentRound?.player.clientType === "newGuest" ||
      currentRound?.player.clientType === "unknownGuest"
        ? hostingRound?.isNonPlayingHost ?? false
        : currentRound?.isNonPlayingHost ?? false,
    transportationMemberId: currentRound?.player.id,
    transportationPlayerType: getBundledTransportationPlayerTypeFromClientType(
      currentRound?.player.clientType
    ),
  });

  return (
    <Suspense fallback={<BundledRateAndTransportSelectSkeleton />}>
      {queryRef && rateOptionsQueryRef ? (
        <BundledRateAndTransportSelectInner
          booking={booking}
          queryRef={queryRef}
          rateOptionsQueryRef={rateOptionsQueryRef}
          roundId={roundId}
          isTargetingAll={isTargetingAll}
          setIsTargetingAll={setIsTargetingAll}
        />
      ) : (
        <BundledRateAndTransportSelectSkeleton />
      )}
    </Suspense>
  );
};

type BundledRateAndTransportSelectInnerProps = {
  booking: BundledRateAndTransportSelect_Booking$data;
  queryRef: PreloadedQuery<useSetValidTransportationTypeQuery>;
  rateOptionsQueryRef: PreloadedQuery<useGetBundledRateOptionsQuery>;
  roundId: string;
  isTargetingAll: boolean;
  setIsTargetingAll: (value: boolean) => void;
};

const BundledRateAndTransportSelectInner = ({
  booking,
  queryRef,
  rateOptionsQueryRef,
  roundId,
  isTargetingAll,
  setIsTargetingAll,
}: BundledRateAndTransportSelectInnerProps) => {
  const currentCourseLayout = useAtomValue(
    getCourseLayoutFromRoundIdAtom(roundId)
  );
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentSessionMemberShipTypeId = useAtomValue(
    currentSessionMemberShipTypeIdAtom
  );
  const defaultMembershipTypeId = useAtomValue(defaultMembershipTypeIdAtom);
  const currentRate = useAtomValue(getRateFromRoundIdAtom(roundId));
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const rateOptionsData = usePreloadedQuery(
    useGetBundledRateOptionsQueryString,
    rateOptionsQueryRef
  );
  const selectedTransportationTypeId = useAtomValue(
    getTransportationTypeFromRoundIdAtom(roundId)
  );
  const setRate = useSetAtom(setPlayerRateOnRoundAtom);
  const setTransportationType = useSetAtom(setTransportationTypeOnRoundAtom);
  const transportationTypes = useRoundTransportationTypesFromQuery(queryRef);
  const isCurrentRateLocked = currentRate?.isLocked;

  const getRoundMembershipTypeId = (round: PatronEditBooking.Round | null) => {
    if (!round) return null;

    const clientType = round.player.clientType;

    if (clientType === "customer") {
      return round.player.membershipTypeId;
    }

    if (clientType === "unknownCustomer") {
      return defaultMembershipTypeId ?? null;
    }

    if (clientType === "member") {
      return (
        round.player.membershipTypeId ??
        getMembershipTypeIdForRound(booking, round.serverId) ??
        currentSessionMemberShipTypeId ??
        null
      );
    }
    if (clientType === "memberHold") {
      return currentSessionMemberShipTypeId ?? null;
    }
    return (
      getMembershipTypeIdForPlayerId(booking, round.hostId) ??
      currentSessionMemberShipTypeId ??
      null
    );
  };

  const getRateEligibilityKey = (round: PatronEditBooking.Round | null) => {
    if (!round) return "null";
    const hostingRound = currentBooking?.rounds.find(
      (bookingRound) => bookingRound.player.id === round.hostId
    );
    const nonPlayingHost =
      round.player.clientType === "guest" ||
      round.player.clientType === "newGuest" ||
      round.player.clientType === "unknownGuest"
        ? hostingRound?.isNonPlayingHost ?? false
        : round.isNonPlayingHost ?? false;
    return `${
      getRoundMembershipTypeId(round) ?? "null"
    }|${getBundledRateTypeFromClientType(
      round.player.clientType
    )}|${nonPlayingHost}`;
  };

  const getRateEligibilityKeyIgnoringMembership = (
    round: PatronEditBooking.Round | null
  ) => {
    if (!round) return "null";
    const hostingRound = currentBooking?.rounds.find(
      (bookingRound) => bookingRound.player.id === round.hostId
    );
    const nonPlayingHost =
      round.player.clientType === "guest" ||
      round.player.clientType === "newGuest" ||
      round.player.clientType === "unknownGuest"
        ? hostingRound?.isNonPlayingHost ?? false
        : round.isNonPlayingHost ?? false;
    return `${getBundledRateTypeFromClientType(
      round.player.clientType
    )}|${nonPlayingHost}`;
  };

  const getTargetRateRoundIds = (rateId: string) => {
    if (!isTargetingAll || !currentRound || !currentBooking) {
      return undefined;
    }

    const selectedRateVisibility = rateOptionsData.slot?.rates?.find(
      (rate) => rate?.rate?.id === rateId
    )?.rate?.visibility;

    const sourceEligibilityKey =
      selectedRateVisibility === "ALL"
        ? getRateEligibilityKeyIgnoringMembership(currentRound)
        : getRateEligibilityKey(currentRound);

    return currentBooking.rounds
      .filter((round) => {
        const targetEligibilityKey =
          selectedRateVisibility === "ALL"
            ? getRateEligibilityKeyIgnoringMembership(round)
            : getRateEligibilityKey(round);
        return targetEligibilityKey === sourceEligibilityKey;
      })
      .map((round) => round.localId);
  };

  const targetTransportationRoundIds = getTargetTransportationRoundIds({
    booking: currentBooking,
    currentRound,
    isTargetingAll,
  });

  const singleOption = useGetSingleBundleRateOption(
    currentRound,
    currentCourseLayout?.holeCount ?? "EIGHTEEN",
    rateOptionsQueryRef,
    selectedTransportationTypeId ?? "",
    transportationTypes
  );

  const bundledOptions = useGetBundledRateOptions(
    currentCourseLayout?.holeCount ?? "EIGHTEEN",
    rateOptionsQueryRef,
    currentRound,
    transportationTypes
  );

  const optionsToUse = isCurrentRateLocked ? [singleOption] : bundledOptions;

  const fallbackOption = (() => {
    const firstOption = optionsToUse[0];
    if (!firstOption) return "";

    // Check if it's a grouped option
    if ("options" in firstOption) {
      return firstOption.options[0]?.value ?? "";
    }

    // It's a regular option
    return firstOption.value;
  })();

  const availableCombinedIds = optionsToUse.flatMap((option) => {
    if ("options" in option) {
      return option.options.flatMap((childOption) =>
        childOption.value ? [childOption.value] : []
      );
    }

    return option.value ? [option.value] : [];
  });

  const currentRoundCombinedId =
    currentRate?.id && selectedTransportationTypeId
      ? `${currentRate.id}.${selectedTransportationTypeId}`
      : null;
  const transportationPreservingFallback = selectedTransportationTypeId
    ? availableCombinedIds.find((id) =>
        id.endsWith(`.${selectedTransportationTypeId}`)
      )
    : undefined;
  const currentCombinedId =
    (currentRoundCombinedId &&
    availableCombinedIds.includes(currentRoundCombinedId)
      ? currentRoundCombinedId
      : null) ??
    transportationPreservingFallback ??
    fallbackOption;

  const { hasDynamicLockedRound, isLockedDynamicRate } = useAtomValue(
    getDynamicLockedRoundIdsAtom(roundId)
  );
  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  return (
    <VStack flex={1}>
      <DropdownSelect
        hideOuterMargin
        hideChevron
        includeLeftElementInSelectedValue
        isViewOnly={isCurrentRateLocked || isLockedDynamicRate || isAlreadyPaid}
        isDisabled={isLockedDynamicRate}
        options={optionsToUse}
        value={currentCombinedId}
        optionalToggle={
          hasDynamicLockedRound
            ? undefined
            : {
                initiallyChecked: isTargetingAll,
                onToggle: setIsTargetingAll,
              }
        }
        onChange={(value) => {
          if (value) {
            const [rateId, ttId] = value.split(".");
            setRate({
              roundId,
              rate: { id: rateId },
              isTargetingAll: isTargetingAll,
              targetRoundIds: getTargetRateRoundIds(rateId),
            });
            setTransportationType({
              roundId,
              transportationTypeId: ttId,
              isTargetingAll: isTargetingAll,
              targetRoundIds: targetTransportationRoundIds,
            });
          }
        }}
        testID="Bundled Rate Dropdown Selector"
      />
    </VStack>
  );
};

const BundledRateAndTransportSelectSkeleton = () => (
  <VStack flex={1}>
    <SkeletonLoader borderRadius="2" width={341} height={48} />
  </VStack>
);

const BundledRateAndTransportSelectFragmentString = graphql`
  fragment BundledRateAndTransportSelect_Booking on Booking {
    startSlot {
      id
    }
    rounds {
      edges {
        node {
          id
          player {
            id
            ... on Customer {
              membershipType {
                id
              }
            }
            ... on Member {
              membershipType {
                id
              }
            }
          }
        }
      }
    }
  }
`;
